import { useMemo, useCallback } from "react";
import { STATUS_LIST } from "../../ProjectList/CanvasKanban";
import { defaultPriorities } from "@/types/priorities";
import { getTShirtSize } from "@/types/issues";
import type { ProjectUser, ProjectLabel } from "../../ProjectList/types";
import type { Project } from "@/utils/types";
import { getProjectLabels, getProjectUsers } from "@/utils/typeGuards";
import { FilterType, DueDateFilterValue } from "@/types/filterEnums";

const DUE_DATE_DISPLAY_MAP = {
  [DueDateFilterValue.NONE]: "No Due Date",
  [DueDateFilterValue.OVERDUE]: "Overdue",
  [DueDateFilterValue.TODAY]: "Due Today",
  [DueDateFilterValue.THIS_WEEK]: "This Week",
  [DueDateFilterValue.NEXT_WEEK]: "Next Week",
  [DueDateFilterValue.LATER]: "Later",
} as const;

type FilterValue = string | number;

interface UseFilterDisplayTextProps {
  currentProject: Project | null;
}

export const useFilterDisplayText = ({
  currentProject,
}: UseFilterDisplayTextProps) => {
  const statusDisplayMap = useMemo(() => {
    return new Map(STATUS_LIST.map((status) => [status.id, status.name]));
  }, []);

  const priorityDisplayMap = useMemo(() => {
    const map = new Map(
      defaultPriorities.map((priority) => [priority.id, priority.name]),
    );
    map.set(0, "No Priority");
    return map;
  }, []);

  const userLookupMap = useMemo(() => {
    const users = getProjectUsers(currentProject?.users);
    return new Map(users.map((user) => [user.id, user]));
  }, [currentProject?.users]);

  const labelLookupMap = useMemo(() => {
    const labels = getProjectLabels(currentProject?.labels);
    return new Map(labels.map((label) => [label.id, label]));
  }, [currentProject?.labels]);

  const displayTextStrategies = useMemo(
    () => ({
      [FilterType.ASSIGNEE]: (value: FilterValue): string => {
        if (value === DueDateFilterValue.NONE) return "none";
        const user = userLookupMap.get(String(value));
        return user?.name || String(value);
      },
      [FilterType.STATUS]: (value: FilterValue): string => {
        return statusDisplayMap.get(String(value)) || String(value);
      },
      [FilterType.PRIORITY]: (value: FilterValue): string => {
        const priorityValue =
          typeof value === "string" ? parseInt(value) : value;
        return priorityDisplayMap.get(priorityValue) || String(value);
      },
      [FilterType.ESTIMATE]: (value: FilterValue): string => {
        const estimateValue =
          typeof value === "string" ? parseInt(value) : value;
        if (estimateValue === 0) return "No Estimate";
        return getTShirtSize(estimateValue) || String(value);
      },
      [FilterType.LABEL]: (value: FilterValue): string => {
        const label = labelLookupMap.get(String(value));
        return label?.label || String(value);
      },
      [FilterType.DUE_DATE]: (value: FilterValue): string => {
        return (
          DUE_DATE_DISPLAY_MAP[
            String(value) as keyof typeof DUE_DATE_DISPLAY_MAP
          ] || String(value)
        );
      },
      [FilterType.SEARCH]: (value: FilterValue): string => String(value),
    }),
    [userLookupMap, labelLookupMap, statusDisplayMap, priorityDisplayMap],
  );

  const getFilterDisplayText = useCallback(
    (filterType: string, value: FilterValue): string => {
      const strategy =
        displayTextStrategies[filterType as keyof typeof displayTextStrategies];
      return strategy ? strategy(value) : String(value);
    },
    [displayTextStrategies],
  );

  return {
    getFilterDisplayText,
    userLookupMap,
    labelLookupMap,
    statusDisplayMap,
    priorityDisplayMap,
  };
};
